import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, CheckCircle2, Download, Mail, MapPin, MessageCircle, X } from "lucide-react";
import { PROFILE, SCOPES } from "../data/cv";
import { fadeUp, stagger, viewportOnce } from "../lib/anim";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PURPOSES = [
  "Recruitment — full-time / leadership role",
  "Consulting engagement",
  "Brand & audience collaboration",
  "Tele-consulting / reporting",
  "Media, speaking or teaching",
  "Other",
];

export default function Engagement() {
  const [scope, setScope] = useState(SCOPES[0]);
  const [form, setForm] = useState({ name: "", email: "", org: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const [cvOpen, setCvOpen] = useState(false);
  const [cvForm, setCvForm] = useState({ name: "", email: "", company: "", phone: "", purpose: PURPOSES[0] });
  const [cvSending, setCvSending] = useState(false);
  const [cvDone, setCvDone] = useState(false);

  useEffect(() => {
    const fn = (e) => {
      if (SCOPES.includes(e.detail)) setScope(e.detail);
    };
    window.addEventListener("select-scope", fn);
    return () => window.removeEventListener("select-scope", fn);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API}/inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, scope }),
      });
      if (!res.ok) throw new Error("failed");
      setSent(true);
      toast.success("Message received — Dr. Rahul replies personally.");
    } catch {
      toast.error("Could not send just now — please email hi@rahulkanungo.com");
    } finally {
      setSending(false);
    }
  };

  const closeCv = () => {
    if (cvSending) return;
    setCvOpen(false);
    setTimeout(() => setCvDone(false), 350);
  };

  const cvSubmit = async (e) => {
    e.preventDefault();
    setCvSending(true);
    try {
      const res = await fetch(`${API}/cv-download`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(cvForm),
      });
      if (!res.ok) throw new Error("failed");
      const a = document.createElement("a");
      a.href = "/Dr-Rahul-Kanungo-CV.pdf";
      a.download = "Dr-Rahul-Kanungo-CV.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setCvDone(true);
      toast.success("CV downloaded — a copy is on its way to your inbox.");
    } catch {
      toast.error("Download could not start — please email hi@rahulkanungo.com");
    } finally {
      setCvSending(false);
    }
  };

  const inputCls =
    "w-full rounded-xl bg-white/[0.04] border border-white/10 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-neon/50 focus:ring-2 focus:ring-neon/20 transition-all duration-300";

  return (
    <section id="engage" className="relative py-16 sm:py-24 lg:py-32">
      <div className="absolute top-10 -left-48 w-[520px] h-[520px] rounded-full bg-vio/10 orb" />
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 relative">
        <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={viewportOnce}>
          <motion.p variants={fadeUp} className="font-mono text-xs tracking-[0.22em] uppercase text-neon mb-4 flex items-center gap-3">
            <span className="w-8 h-px bg-gradient-to-r from-neon to-transparent" /> 07 — Engage
          </motion.p>
          <motion.h2 variants={fadeUp} custom={1} className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight max-w-3xl">
            Hard problems in healthcare deserve <span className="text-grad">engineered answers. Bring yours.</span>
          </motion.h2>
          <motion.p variants={fadeUp} custom={2} className="mt-4 text-sm sm:text-base text-slate-400 max-w-2xl flex items-center gap-2">
            <MapPin size={15} className="text-neon shrink-0" />
            Open to administrative &amp; leadership responsibilities and long-term collaborations — onsite in Bhubaneswar, Odisha; remote, sponsored-visit &amp; project-based across India and worldwide.
          </motion.p>
        </motion.div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Scopes + CV + direct contacts */}
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce} className="lg:col-span-5 flex flex-col gap-6">
            <div className="panel rounded-2xl p-6 sm:p-8" data-testid="scopes-card">
              <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-slate-500 mb-5">Ways we can work</p>
              <div className="space-y-3">
                {SCOPES.map((s) => (
                  <button
                    key={s}
                    data-testid={`scope-option-${s.split(" ")[0].toLowerCase()}`}
                    onClick={() => setScope(s)}
                    className={`w-full text-left flex items-center justify-between rounded-xl px-4 py-3.5 text-sm border transition-all duration-300 ${
                      scope === s
                        ? "border-neon/50 bg-neon/[0.07] text-white"
                        : "border-white/10 bg-white/[0.02] text-slate-400 hover:border-white/25 hover:text-slate-200"
                    }`}
                  >
                    {s}
                    <ArrowUpRight size={14} className={scope === s ? "text-neon" : "text-slate-600"} />
                  </button>
                ))}
              </div>
            </div>

            <div className="panel spot-card rounded-2xl p-6 sm:p-8" data-testid="cv-download-card">
              <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-slate-500 mb-3">CV, on the record</p>
              <p className="text-sm text-slate-400 leading-relaxed">
                The full career profile — degrees, experience, publications and certifications. One quick form and it
                downloads instantly, with a copy and a personal note emailed to you.
              </p>
              <button
                data-testid="cv-download-button"
                onClick={() => setCvOpen(true)}
                className="mt-5 flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-ink bg-gradient-to-r from-neon to-vio hover:shadow-glow-cyan transition-shadow duration-300"
              >
                <Download size={15} /> Download CV
              </button>
            </div>

            <div className="panel rounded-2xl p-6 sm:p-8 space-y-3" data-testid="direct-contact-card">
              <a href={`mailto:${PROFILE.email}`} data-testid="contact-email-link" className="flex items-center gap-3 rounded-xl border border-white/10 px-4 py-3.5 text-sm text-slate-300 hover:border-neon/40 hover:text-white transition-colors duration-300">
                <Mail size={15} className="text-neon" /> {PROFILE.email}
              </a>
              <a href={PROFILE.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-link" className="flex items-center gap-3 rounded-xl border border-white/10 px-4 py-3.5 text-sm text-slate-300 hover:border-emerald-400/40 hover:text-white transition-colors duration-300">
                <MessageCircle size={15} className="text-emerald-400" /> WhatsApp {PROFILE.phone}
              </a>
            </div>
          </motion.div>

          {/* Form */}
          <motion.div variants={fadeUp} custom={1} initial="hidden" whileInView="show" viewport={viewportOnce} className="lg:col-span-7">
            <div className="panel rounded-2xl p-6 sm:p-8 h-full" data-testid="engagement-form-card">
              {sent ? (
                <div className="h-full min-h-[380px] flex flex-col items-center justify-center text-center" data-testid="engagement-success-panel">
                  <CheckCircle2 size={44} className="text-emerald-400" />
                  <h3 className="mt-5 font-display text-xl font-semibold">Your brief is with Dr. Rahul.</h3>
                  <p className="mt-2 text-sm text-slate-400 max-w-sm">
                    Expect a personal reply — usually within a day. For anything urgent, WhatsApp {PROFILE.phone}.
                  </p>
                  <button onClick={() => { setSent(false); setForm({ name: "", email: "", org: "", message: "" }); }} className="mt-7 text-sm text-neon hover:text-white transition-colors" data-testid="engagement-send-another-button">
                    Send another brief →
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5" data-testid="engagement-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <input required minLength={2} placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="engagement-form-name-input" />
                    <input required type="email" placeholder="Work email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="engagement-form-email-input" />
                  </div>
                  <input placeholder="Organisation / startup (optional)" value={form.org} onChange={(e) => setForm({ ...form, org: e.target.value })} className={inputCls} data-testid="engagement-form-org-input" />
                  <div className="rounded-xl border border-neon/25 bg-neon/[0.05] px-4 py-3 text-xs font-mono text-neon" data-testid="engagement-selected-scope">
                    Scope → {scope}
                  </div>
                  <textarea required minLength={2} rows={5} placeholder="What are we solving? Role, contract, product, brand campaign or talk…" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={`${inputCls} resize-none`} data-testid="engagement-form-message-input" />
                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="engagement-form-submit"
                    className="w-full sm:w-auto flex items-center justify-center gap-2 rounded-full px-8 py-3.5 font-semibold text-ink bg-gradient-to-r from-neon to-vio hover:shadow-glow-cyan transition-shadow duration-300 disabled:opacity-60"
                  >
                    {sending ? "Sending…" : "Send the brief"}
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      {/* CV download modal */}
      <AnimatePresence>
        {cvOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[70] grid place-items-center p-4"
            data-testid="cv-modal"
          >
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={closeCv} data-testid="cv-modal-overlay" />
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 30, scale: 0.97 }}
              transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
              className="relative glass rounded-2xl w-full max-w-md p-6 sm:p-8 max-h-[90vh] overflow-y-auto"
              data-testid="cv-modal-panel"
            >
              <button onClick={closeCv} data-testid="cv-modal-close-button" className="absolute top-4 right-4 text-slate-500 hover:text-white transition-colors">
                <X size={18} />
              </button>

              {cvDone ? (
                <div className="text-center py-6" data-testid="cv-success-panel">
                  <CheckCircle2 size={40} className="text-emerald-400 mx-auto" />
                  <h3 className="mt-4 font-display text-lg font-semibold">CV downloaded.</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">
                    A copy is on its way to <span className="text-slate-200">{cvForm.email}</span> with a personal note
                    from Dr. Rahul. Recruiters' details reach him directly — expect a follow-up if there is a fit.
                  </p>
                  <button onClick={closeCv} data-testid="cv-success-close-button" className="mt-6 rounded-full px-6 py-2.5 text-sm font-semibold text-ink bg-gradient-to-r from-neon to-vio">
                    Done
                  </button>
                </div>
              ) : (
                <form onSubmit={cvSubmit} className="space-y-4" data-testid="cv-form">
                  <div>
                    <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-neon mb-1">Download the CV</p>
                    <p className="text-xs text-slate-500">A copy lands in your inbox with Dr. Rahul's compliments. Your details reach him directly.</p>
                  </div>
                  <input required minLength={2} placeholder="Your name" value={cvForm.name} onChange={(e) => setCvForm({ ...cvForm, name: e.target.value })} className={inputCls} data-testid="cv-form-name-input" />
                  <input required type="email" placeholder="Work email" value={cvForm.email} onChange={(e) => setCvForm({ ...cvForm, email: e.target.value })} className={inputCls} data-testid="cv-form-email-input" />
                  <input required minLength={2} placeholder="Company / organisation" value={cvForm.company} onChange={(e) => setCvForm({ ...cvForm, company: e.target.value })} className={inputCls} data-testid="cv-form-company-input" />
                  <input required type="tel" minLength={5} placeholder="Contact number" value={cvForm.phone} onChange={(e) => setCvForm({ ...cvForm, phone: e.target.value })} className={inputCls} data-testid="cv-form-phone-input" />
                  <select value={cvForm.purpose} onChange={(e) => setCvForm({ ...cvForm, purpose: e.target.value })} className={`${inputCls} appearance-none`} data-testid="cv-form-purpose-select">
                    {PURPOSES.map((p) => (
                      <option key={p} value={p} className="bg-panel text-slate-100">{p}</option>
                    ))}
                  </select>
                  <button
                    type="submit"
                    disabled={cvSending}
                    data-testid="cv-form-submit"
                    className="w-full flex items-center justify-center gap-2 rounded-full px-8 py-3.5 font-semibold text-ink bg-gradient-to-r from-neon to-vio hover:shadow-glow-cyan transition-shadow duration-300 disabled:opacity-60"
                  >
                    <Download size={15} /> {cvSending ? "Preparing…" : "Get the CV"}
                  </button>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}